import { useState } from 'react';
import { ImageIcon, Music, Play } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useHasAnyRole } from '@/auth/hooks';
import { AppRole } from '@/auth/roles';
import type { MediaFile, ModerationDetail } from '../types';
import type { ModerationActions } from '../hooks/useModerationActions';
import { Field } from '../components/DetailField';
import { MediaViewerDialog, type MediaViewerState } from '../components/MediaViewerDialog';
import { formatDateTime, isContentHidden, isContentRemoved } from '../lib/display';
import { safeMediaSrc } from '@/lib/mediaUrl';

function mediaState(m: MediaFile): MediaViewerState {
  return { url: m.fileUrl ?? m.thumbnail ?? '', isVideo: m.kind === 'VID', playbackId: m.playbackId };
}

function MediaTile({ media, onOpen }: { media: MediaFile; onOpen: () => void }) {
  const thumb = safeMediaSrc(media.thumbnail ?? (media.kind === 'IMG' ? media.fileUrl : null));
  return (
    <button
      type="button"
      onClick={onOpen}
      className="relative aspect-square overflow-hidden rounded-lg border bg-muted"
    >
      {thumb ? (
        <img src={thumb} alt="" className="size-full object-cover" loading="lazy" />
      ) : (
        <div className="flex size-full items-center justify-center text-muted-foreground">
          {media.kind === 'AUD' ? <Music className="size-6" /> : <ImageIcon className="size-6" />}
        </div>
      )}
      {media.kind === 'VID' && (
        <span className="absolute inset-0 flex items-center justify-center bg-black/30 text-white">
          <Play className="size-6" />
        </span>
      )}
    </button>
  );
}

/**
 * Reported content card (doc 09 §4.5). Media grid (per-media zoom / video viewer), key/value info rows,
 * and the auto-swapping Take-Down ↔ List button keyed off postStatus === HID.
 */
export function ReportedContentCard({
  report,
  actions,
}: {
  report: ModerationDetail;
  actions: ModerationActions;
}) {
  const [viewer, setViewer] = useState<MediaViewerState | null>(null);
  const canModerate = useHasAnyRole([AppRole.Administrator, AppRole.SystemAdmin, AppRole.ContentManager]);

  if (report.kind !== 'CONTENT') return null;
  const c = report.content;
  const hidden = isContentHidden(c.status);
  const removed = isContentRemoved(c.status);
  const coverThumb = safeMediaSrc(c.thumbnail);
  const t = c.totals;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Reported Content</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {c.mediaFiles.length > 0 ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-3">
            {c.mediaFiles.map((m) => (
              <MediaTile key={m.id} media={m} onOpen={() => setViewer(mediaState(m))} />
            ))}
          </div>
        ) : coverThumb ? (
          <img src={coverThumb} alt="" className="max-h-64 w-full rounded-lg border object-cover" />
        ) : null}

        <div className="divide-y">
          {c.title && c.title !== '—' && <Field label="Title / Content">{c.title}</Field>}
          <Field label="Type">{c.type}</Field>
          <Field label="Status">
            <Badge variant={hidden || removed ? 'destructive' : 'default'}>{c.status}</Badge>
          </Field>
          <Field label="Upload Date">{formatDateTime(c.uploadDate)}</Field>
          <Field label="Price">{c.price}</Field>
          <Field label="Country">{c.country}</Field>
          <Field label="IP">{c.ip}</Field>
          <Field label="Device">{c.device}</Field>
          {c.collaborator && <Field label="Collaborator">{c.collaborator}</Field>}
          {c.hashtags && <Field label="Hashtags">{c.hashtags}</Field>}
          <Field label="Engagement">
            {`${t.view} views · ${t.like} likes · ${t.comment} comments · ${t.share} shares · ${t.bookmark} saves`}
          </Field>
          {c.totalEarned && <Field label="Total Earned">{c.totalEarned}</Field>}
          {c.totalTips && <Field label="Total Tips">{c.totalTips}</Field>}
          {c.releaseTime && <Field label="Release Time">{formatDateTime(c.releaseTime)}</Field>}
          {c.expirationTime && <Field label="Expiration Time">{formatDateTime(c.expirationTime)}</Field>}
        </div>

        {canModerate && !removed && (
          <Button
            className="w-full"
            variant={hidden ? 'default' : 'destructive'}
            disabled={actions.isPending}
            onClick={() => void (hidden ? actions.listContent() : actions.takeDown())}
          >
            {hidden ? 'List Content' : 'Take Down'}
          </Button>
        )}

        {canModerate && (
          <Button
            className="w-full"
            variant="destructive"
            disabled={actions.isPending || removed}
            onClick={() => void actions.removeContent()}
          >
            {removed ? 'Content Removed' : 'Remove Content'}
          </Button>
        )}
      </CardContent>

      <MediaViewerDialog media={viewer} onClose={() => setViewer(null)} />
    </Card>
  );
}
